import { API } from "../config/api.js";
import { get } from "../lib/http.js";

export const ORGANIZER_APPROVAL_REQUIRED = "ORGANIZER_APPROVAL_REQUIRED";
export const AGREEMENT_PDF_PATH = "/agreements/organizer-agreement.pdf";

export const UPLOAD_KINDS = {
  panCard: "panCard",
  cancelledCheque: "cancelledCheque",
  signedAgreement: "signedAgreement",
};

export const ACCOUNT_TYPES = [
  { value: "savings", label: "Savings" },
  { value: "current", label: "Current" },
];

export const BANK_NAMES = [
  "State Bank of India",
  "HDFC Bank",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Punjab National Bank",
  "Bank of Baroda",
  "Canara Bank",
  "Union Bank of India",
  "Bank of India",
  "IDFC FIRST Bank",
  "Yes Bank",
  "IndusInd Bank",
  "Federal Bank",
  "Indian Bank",
  "Central Bank of India",
  "UCO Bank",
  "Indian Overseas Bank",
  "Bandhan Bank",
  "Other",
];

/**
 * Fields admins can tag with @ in rejection remarks.
 * `key` is stored on the application; `token` is what appears after @.
 * Documents listed first so @PAN-style searches surface PAN card document clearly.
 */
export const REJECTABLE_FIELDS = [
  { key: "panCard", token: "PANCard", label: "PAN card document (upload)", step: 2 },
  { key: "cancelledCheque", token: "CancelledCheque", label: "Cancelled cheque (upload)", step: 2 },
  { key: "signedAgreement", token: "SignedAgreement", label: "Signed agreement (upload)", step: 3 },
  { key: "entityType", token: "EntityType", label: "Entity type", step: 1 },
  { key: "legalName", token: "LegalName", label: "Organisation / Individual name", step: 1 },
  { key: "pan", token: "PANNumber", label: "PAN number (text field)", step: 1 },
  { key: "organisationAddress", token: "Address", label: "Organisation address", step: 1 },
  { key: "hasGstin", token: "HasGSTIN", label: "Has GSTIN", step: 1 },
  { key: "gstin", token: "GSTIN", label: "GSTIN number", step: 1 },
  { key: "filedLastTwoYearsItr", token: "ITR", label: "ITR filing (2 years)", step: 1 },
  { key: "contactFullName", token: "ContactName", label: "Contact full name", step: 1 },
  { key: "contactEmail", token: "ContactEmail", label: "Contact email", step: 1 },
  { key: "contactMobile", token: "ContactMobile", label: "Contact mobile", step: 1 },
  { key: "bankBeneficiaryName", token: "Beneficiary", label: "Beneficiary name", step: 1 },
  { key: "bankAccountType", token: "AccountType", label: "Account type", step: 1 },
  { key: "bankName", token: "BankName", label: "Bank name", step: 1 },
  { key: "bankAccountNumber", token: "AccountNumber", label: "Account number", step: 1 },
  { key: "bankIfsc", token: "IFSC", label: "Bank IFSC", step: 1 },
];

export const REJECTABLE_FIELD_KEYS = REJECTABLE_FIELDS.map((f) => f.key);

export function findRejectableField(query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return REJECTABLE_FIELDS;
  const scored = REJECTABLE_FIELDS.map((f) => {
    const token = f.token.toLowerCase();
    const label = f.label.toLowerCase();
    const key = f.key.toLowerCase();
    let score = 0;
    if (token === q || key === q) score = 100;
    else if (token.startsWith(q) || key.startsWith(q)) score = 80;
    else if (token.includes(q) || key.includes(q) || label.includes(q)) score = 40;
    // Prefer document when typing "pan" / "card"
    if ((q === "pan" || q.startsWith("panc")) && f.key === "panCard") score += 30;
    if ((q === "pan" || q === "pannumber") && f.key === "pan") score += 10;
    return { f, score };
  }).filter((x) => x.score > 0);
  scored.sort((a, b) => b.score - a.score);
  return scored.map((x) => x.f);
}

function resolveRejectToken(token) {
  const t = String(token || "").toLowerCase();
  // Ambiguous "@PAN" → PAN card document (what admins usually mean when rejecting)
  if (t === "pan") return "panCard";
  if (t === "pannumber" || t === "panno") return "pan";
  const field = REJECTABLE_FIELDS.find(
    (f) => f.token.toLowerCase() === t || f.key.toLowerCase() === t
  );
  return field?.key || null;
}

/** Parse @Tokens (+ plain "pan card" phrases) into unique rejectable field keys. */
export function parseRejectedFieldsFromRemark(remark) {
  const text = String(remark || "");
  const keys = [];
  const add = (key) => {
    if (key && !keys.includes(key)) keys.push(key);
  };

  const matches = text.match(/@([A-Za-z][A-Za-z0-9_]*)/g) || [];
  for (const raw of matches) {
    add(resolveRejectToken(raw.slice(1)));
  }

  // Phrase heuristics so "invalid pan card" flags the upload even without @
  const lower = text.toLowerCase();
  if (/pan\s*card/.test(lower)) add("panCard");
  if (/cancelled\s*cheque|cancel\s*cheque/.test(lower)) add("cancelledCheque");
  if (/signed\s*agreement|agreement\s*pdf/.test(lower)) add("signedAgreement");

  return keys;
}

export function isFieldRejected(app, fieldKey) {
  const list = Array.isArray(app?.rejectedFields) ? app.rejectedFields : [];
  if (list.includes(fieldKey)) return true;

  // Back-compat: older rejects may store "pan" when admin meant PAN card document
  const reason = String(app?.rejectionReason || "").toLowerCase();
  if (fieldKey === "panCard") {
    if (/pan\s*card|@pancard/.test(reason)) return true;
    if (list.includes("pan") && /(card|document|upload|scan|image|jpeg|pdf)/.test(reason)) {
      return true;
    }
  }
  // Don't highlight PAN number when remark clearly targets the card document
  if (fieldKey === "pan") {
    if (!list.includes("pan")) return false;
    if (/pan\s*card|@pancard/.test(reason) && !/pan\s*(number|no\.?|num)/.test(reason)) {
      return false;
    }
  }
  return false;
}

export function rejectedFieldLabels(app) {
  return REJECTABLE_FIELDS.filter((f) => isFieldRejected(app, f.key)).map((f) => f.label);
}

/** All field keys currently considered rejected (including remark heuristics). */
export function allRejectedFieldKeys(app) {
  return REJECTABLE_FIELDS.filter((f) => isFieldRejected(app, f.key)).map((f) => f.key);
}

export function storedUser() {
  try {
    return JSON.parse(localStorage.getItem("bb_user") || "null");
  } catch {
    return null;
  }
}

export function storedRole() {
  return String(storedUser()?.role || "").toLowerCase();
}

export function isSuperadmin() {
  return storedRole() === "superadmin";
}

export function normalizeApplication(raw) {
  if (!raw || typeof raw !== "object") return null;
  const app = raw.application || raw.organizerApplication || raw;
  if (!app || typeof app !== "object") return null;
  return app;
}

export function applicationStatus(app) {
  return String(app?.status || "draft").toLowerCase();
}

export function isApproved(app) {
  return applicationStatus(app) === "approved";
}

export function hasPanDoc(app) {
  return Boolean(app?.documents?.panCardUrl);
}

export function hasChequeDoc(app) {
  return Boolean(app?.documents?.cancelledChequeUrl);
}

export function hasAgreementDoc(app) {
  return Boolean(app?.documents?.signedAgreementUrl);
}

export function hasGeneralDetails(app) {
  if (!app) return false;
  return Boolean(
    app.entityType &&
      app.legalName &&
      app.pan &&
      app.organisationAddress &&
      typeof app.hasGstin === "boolean" &&
      typeof app.filedLastTwoYearsItr === "boolean" &&
      app.contact?.fullName &&
      app.contact?.email &&
      app.contact?.mobile &&
      app.bank?.beneficiaryName &&
      app.bank?.accountType &&
      app.bank?.bankName &&
      app.bank?.accountNumber &&
      app.bank?.ifsc
  );
}

/** Infer which wizard step to resume for draft/rejected applications. */
export function inferWizardStep(app) {
  if (!hasGeneralDetails(app)) return 1;
  if (!hasPanDoc(app) || !hasChequeDoc(app)) return 2;
  return 3;
}

export async function fetchMyOrganizerApplication() {
  const response = await get(API.organizerApplication.me);
  return normalizeApplication(response);
}

export function isOrganizerApprovalError(error) {
  return (
    error?.code === ORGANIZER_APPROVAL_REQUIRED ||
    (error?.status === 403 &&
      /organizer.?approv/i.test(String(error?.message || "")))
  );
}

export function safeDocHref(url) {
  const value = String(url || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("/")) return value;
  return "";
}

export function fileNameFromUrl(url) {
  try {
    const path = String(url || "").split("?")[0];
    const name = path.split("/").pop();
    return name || "Document";
  } catch {
    return "Document";
  }
}
